import React, { useEffect, useState, useCallback, useRef } from "react";

const API = "https://appapi.unpaidfinancial.com/api";

// Gestion des tokens en mémoire (compatible avec artifacts)
let authTokenMemory = null;
let refreshTokenMemory = null;

const getAuthToken = () => authTokenMemory;
const setAuthToken = (token) => { authTokenMemory = token; };
const getRefreshToken = () => refreshTokenMemory;
const setRefreshToken = (token) => { refreshTokenMemory = token; };

// Fonction fetch helper
const fetchAPI = async (url, options = {}) => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      try {
        const refreshResponse = await fetch(`${API}/refresh-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          setAuthToken(refreshData.accessToken);
          setRefreshToken(refreshData.refreshToken);
          headers['Authorization'] = `Bearer ${refreshData.accessToken}`;
          const retryResponse = await fetch(url, { ...options, headers });
          return retryResponse.json();
        }
      } catch (refreshError) {
        console.error("Erreur refresh:", refreshError);
      }
    }
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || error.message || 'Erreur réseau');
  }
  return response.json();
};

// Utils
const fmtMoney = (n, dev = "EUR") =>
  `${Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${dev}`;

const fullName = (u) =>
  (`${u?.prenom ?? ""} ${u?.nom ?? ""}`.trim()) || u?.name || "Utilisateur";

const formatIban = (iban) => iban ? iban.replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim() : "";
const isIbanLike = (val) => !val || /^[A-Za-z]{2}\d{2}[A-Za-z0-9]{0,30}$/.test(val.replace(/\s+/g, ''));

const ACTIONS = [
  { value: "", label: "Sélectionnez une action..." },
  { value: "edit_bank_info", label: "Modifier carte / IBAN" },
  { value: "edit_pin", label: "Modifier le code PIN (Carte)" },
  { value: "send_pin", label: "Envoyer le code PIN par email" },
  { value: "set_block_percent", label: "Définir pourcentage de blocage" },
  { value: "edit_activation_code", label: "Modifier le code d'activation" },
  { value: "send_activation_code", label: "Envoyer code d'activation par email" },
  { value: "send_all_info", label: "📧 Envoyer TOUTES les informations par email" },
  { value: "refund_transfer", label: "💸 Rembourser un transfert en attente" },
  { value: "activate", label: "✅ Activer le compte" },
  { value: "archive", label: "🚫 Archiver (Désactiver)" },
  { value: "delete", label: "🗑️ Supprimer le compte" },
];

export default function AccountsDirectory() {
  const [token, setToken] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAction, setSelectedAction] = useState("");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // États séparés pour éviter le problème de focus
  const [cardNumber, setCardNumber] = useState("");
  const [expMonth, setExpMonth] = useState("");
  const [expYear, setExpYear] = useState("");
  const [brand, setBrand] = useState("");
  const [nameOnCard, setNameOnCard] = useState("");
  const [bin, setBin] = useState("");
  const [iban, setIban] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [newPin, setNewPin] = useState("");
  const [blockPercent, setBlockPercent] = useState(0);
  const [blockMessage, setBlockMessage] = useState("");
  const [newActivationCode, setNewActivationCode] = useState("");
  const [refundReason, setRefundReason] = useState("");

  const [pendingDebits, setPendingDebits] = useState([]);
  const [loadingDebits, setLoadingDebits] = useState(false);
  const [selectedDebits, setSelectedDebits] = useState([]);

  const lastAccountIdRef = useRef(null);

  // Initialisation du token
  useEffect(() => {
    const storedToken = typeof localStorage !== 'undefined' ? localStorage.getItem('accessToken') : null;
    const storedRefresh = typeof localStorage !== 'undefined' ? localStorage.getItem('refreshToken') : null;
    
    if (!storedToken) {
      setAuthError("Vous devez être connecté en tant qu'administrateur.");
      setLoading(false);
      return;
    }
    
    setAuthToken(storedToken);
    setRefreshToken(storedRefresh);
    setToken(storedToken);
  }, []);

  const loadAccounts = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setAuthError(null);
    try {
      const data = await fetchAPI(`${API}/bank/accounts?per_page=50`);
      setItems(data.data || []);
    } catch (e) {
      console.error("Erreur chargement comptes:", e);
      setAuthError(e.message.includes('403') || e.message.includes('401') 
        ? "Accès refusé. Admin requis." 
        : `Erreur: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) loadAccounts();
  }, [token, loadAccounts]);

  useEffect(() => {
    if (!selectedAccount) {
      setPendingDebits([]);
      return;
    }
    setLoadingDebits(true);
    fetchAPI(`${API}/bank/accounts/${selectedAccount.id}/pending-debits`)
      .then((res) => setPendingDebits(res.transactions || []))
      .catch((err) => console.error("Erreur débits:", err))
      .finally(() => setLoadingDebits(false));
  }, [selectedAccount]);

  useEffect(() => {
    if (selectedAccount && selectedAccount.id !== lastAccountIdRef.current) {
      lastAccountIdRef.current = selectedAccount.id;
      const c = selectedAccount.card || {};
      setCardNumber("");
      setExpMonth(c?.exp_month || "");
      setExpYear(c?.exp_year || "");
      setBrand(c?.brand || "");
      setNameOnCard(c?.name_on_card || fullName(selectedAccount.user));
      setBin(c?.bin || "");
      setIban(selectedAccount?.iban || "");
      setBlockPercent(selectedAccount?.block_percent ?? 0);
      setBlockMessage(selectedAccount?.block_message || "");
      setNewPin("");
      setNewActivationCode("");
      setSendEmail(true);
      setRefundReason("");
    }
  }, [selectedAccount]);

  const toggleDebitSelection = (id) => {
    setSelectedDebits((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAction = async (action) => {
    if (!selectedAccount) return alert("Sélectionnez un compte.");
    if (!token) return alert("Token manquant.");

    setIsSubmitting(true);
    const accId = selectedAccount.id;
    const cardId = selectedAccount.card?.id;

    try {
      switch (action) {
        case "refund_transfer": {
          if (selectedDebits.length === 0) {
            alert("❌ Veuillez sélectionner au moins une transaction à rembourser.");
            setIsSubmitting(false);
            return;
          }
          
          const reason = refundReason.trim();
          if (!reason || reason.length < 5) {
            alert("❌ Le motif doit contenir au moins 5 caractères.");
            setIsSubmitting(false);
            return;
          }

          let successCount = 0;
          let errorCount = 0;
          let errorMessages = [];

          for (const id of selectedDebits) {
            try {
              await fetchAPI(`${API}/bank/transactions/${id}/refund`, {
                method: "POST",
                body: JSON.stringify({ reason }),
              });
              successCount++;
            } catch (err) {
              console.error("Erreur remboursement", id, err);
              errorCount++;
              errorMessages.push(`Transaction #${id}: ${err.message}`);
            }
          }

          if (successCount > 0 && errorCount === 0) {
            alert(`✅ ${successCount} transaction(s) remboursée(s) avec succès !`);
            setSelectedDebits([]);
            setRefundReason("");
            
            // Recharge les données
            fetchAPI(`${API}/bank/accounts/${selectedAccount.id}/pending-debits`)
              .then((res) => setPendingDebits(res.transactions || []));
            
            fetchAPI(`${API}/bank/accounts/${selectedAccount.id}`)
              .then((data) => setSelectedAccount(data));
          } else if (successCount > 0 && errorCount > 0) {
            alert(`⚠️ ${successCount} remboursement(s) effectué(s), mais ${errorCount} erreur(s):\n\n${errorMessages.join('\n')}`);
          } else {
            alert(`❌ Échec du remboursement:\n\n${errorMessages.join('\n')}`);
          }
          break;
        }

        case "edit_bank_info": {
          if (!cardId) throw new Error("Carte introuvable.");
          if (iban && !isIbanLike(iban)) throw new Error("IBAN invalide.");
          const payload = {
            ...(cardNumber?.trim() ? { card_number: cardNumber.replace(/\s|-/g, "") } : {}),
            ...(expMonth ? { exp_month: expMonth } : {}),
            ...(expYear ? { exp_year: expYear } : {}),
            ...(brand ? { brand } : {}),
            ...(nameOnCard ? { name_on_card: nameOnCard } : {}),
            ...(bin ? { bin } : {}),
            ...(iban ? { iban: iban.replace(/\s+/g, '').toUpperCase() } : {}),
            send_email: sendEmail,
          };
          await fetchAPI(`${API}/bank/cards/${cardId}`, {
            method: 'PUT',
            body: JSON.stringify(payload),
          });
          alert("✅ Carte / IBAN mis à jour.");
          break;
        }

        case "edit_pin": {
          if (!cardId) throw new Error("Carte introuvable.");
          if (!/^\d{4}$/.test(newPin)) throw new Error("PIN = 4 chiffres.");
          await fetchAPI(`${API}/bank/cards/${cardId}/pin`, {
            method: 'PATCH',
            body: JSON.stringify({ pin: newPin }),
          });
          alert("✅ PIN mis à jour.");
          break;
        }

        case "send_pin": {
          if (!cardId) throw new Error("Carte introuvable.");
          await fetchAPI(`${API}/bank/cards/${cardId}/send-pin`, {
            method: 'POST',
            body: JSON.stringify({}),
          });
          alert("✅ Email PIN envoyé.");
          break;
        }

        case "set_block_percent": {
          const percent = Number(blockPercent);
          if (isNaN(percent) || percent < 0 || percent > 100) {
            throw new Error("Pourcentage 0-100.");
          }
          await fetchAPI(`${API}/bank/accounts/${accId}/block-percent`, {
            method: "PATCH",
            body: JSON.stringify({
              block_percent: percent,
              block_message: blockMessage || "",
            }),
          });
          alert(`✅ Blocage à ${percent}%.`);
          break;
        }

        case "edit_activation_code": {
          await fetchAPI(`${API}/bank/accounts/${accId}/activation-code`, {
            method: 'PATCH',
            body: JSON.stringify({ activation_code: newActivationCode }),
          });
          alert("✅ Code activation mis à jour.");
          break;
        }

        case "send_activation_code": {
          await fetchAPI(`${API}/bank/accounts/${accId}/send-activation-code`, {
            method: 'POST',
            body: JSON.stringify({}),
          });
          alert("✅ Email code activation envoyé.");
          break;
        }

        case "send_all_info": {
          if (!window.confirm("Envoyer TOUTES les infos sensibles par email ?")) {
            setIsSubmitting(false);
            return;
          }
          await fetchAPI(`${API}/bank/accounts/${accId}/send-informations`, {
            method: 'POST',
            body: JSON.stringify({}),
          });
          alert("✅ Email complet envoyé.");
          break;
        }

        case "activate": {
          if (selectedAccount.active) throw new Error("Déjà actif.");
          if (!window.confirm("Activer ce compte ?")) {
            setIsSubmitting(false);
            return;
          }
          await fetchAPI(`${API}/bank/accounts/${accId}/active`, {
            method: 'PATCH',
            body: JSON.stringify({ active: true, reason: "Réactivation admin" }),
          });
          alert("✅ Compte activé.");
          break;
        }

        case "archive": {
          if (!selectedAccount.active) throw new Error("Déjà désactivé.");
          if (!window.confirm("Désactiver ce compte ?")) {
            setIsSubmitting(false);
            return;
          }
          await fetchAPI(`${API}/bank/accounts/${accId}/active`, {
            method: 'PATCH',
            body: JSON.stringify({ active: false, reason: "Archivage admin" }),
          });
          alert("✅ Compte archivé.");
          break;
        }

        case "delete": {
          if (!window.confirm("⚠️ SUPPRESSION DÉFINITIVE ?\n\nIrréversible !")) {
            setIsSubmitting(false);
            return;
          }
          await fetchAPI(`${API}/bank/accounts/${accId}`, {
            method: 'DELETE',
          });
          alert("✅ Compte supprimé.");
          setSelectedAccount(null);
          setSelectedAction("");
          await loadAccounts();
          setIsSubmitting(false);
          return;
        }

        default:
          throw new Error("Action non reconnue.");
      }

      const data = await fetchAPI(`${API}/bank/accounts/${accId}`);
      setSelectedAccount(data);
      await loadAccounts();

    } catch (e) {
      console.error("Erreur action:", e);
      alert(`❌ ${e.message || "Erreur inconnue"}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const ActionForm = () => {
    if (!selectedAccount) return <p className="alert alert-info">Sélectionnez un compte.</p>;

    const acc = selectedAccount;
    const card = acc.card;
    const isCardAction = ['edit_pin', 'edit_bank_info', 'send_pin', 'edit_activation_code'].includes(selectedAction);

    if (!acc.active && !['delete', 'archive', 'activate'].includes(selectedAction)) {
      return (
        <div className="alert alert-warning">
          ⚠️ Compte <strong>inactif</strong>. Réactivez-le d'abord.
        </div>
      );
    }

    if (isCardAction && !card) {
      return <p className="alert alert-warning">⚠️ Pas de carte associée.</p>;
    }

    switch (selectedAction) {
      case "edit_bank_info":
        return (
          <div className="form-grid">
            <div className="form-group">
              <label>Numéro de carte (PAN)</label>
              <input
                className="form-control"
                placeholder="Laisser vide = pas de modif"
                value={cardNumber}
                onChange={(e) => setCardNumber(e.target.value)}
              />
              <small className="text-muted">16 chiffres max</small>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Mois (MM)</label>
                <input
                  className="form-control"
                  maxLength={2}
                  placeholder="01-12"
                  value={expMonth}
                  onChange={(e) => setExpMonth(e.target.value.replace(/\D/g, '').slice(0, 2))}
                />
              </div>

              <div className="form-group">
                <label>Année (AAAA)</label>
                <input
                  className="form-control"
                  maxLength={4}
                  placeholder="2025"
                  value={expYear}
                  onChange={(e) => setExpYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Marque</label>
              <input
                className="form-control"
                placeholder="VISA, MASTERCARD..."
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Nom sur la carte</label>
              <input
                className="form-control"
                value={nameOnCard}
                onChange={(e) => setNameOnCard(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>BIN (3-8 chiffres)</label>
              <input
                className="form-control"
                maxLength={8}
                value={bin}
                onChange={(e) => setBin(e.target.value.replace(/\D/g, '').slice(0, 8))}
                placeholder="Ex: 5222"
              />
            </div>

            <div className="form-group">
              <label>IBAN</label>
              <input
                className="form-control"
                placeholder="FR76 1234 5678 9012 3456 7890 123"
                value={iban}
                onChange={(e) => setIban(e.target.value)}
              />
              <small className="text-muted">2 lettres + 2 chiffres + max 30 car.</small>
            </div>

            <div className="form-check">
              <input
                id="send_email"
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
              />
              <label htmlFor="send_email">Envoyer email notification</label>
            </div>
          </div>
        );

      case "refund_transfer":
        return (
          <div className="form-group">
            <label>Motif du remboursement</label>
            <textarea
              className="form-control"
              rows="3"
              placeholder="Ex: Remboursement administratif suite à une erreur"
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
            />
            <small className="text-muted">
              Cochez les transactions ci-dessus, saisissez un motif (min 5 caractères) et cliquez sur "Exécuter" ci-dessous.
            </small>
          </div>
        );

      case "edit_pin":
        return (
          <>
            <div className="form-group">
              <label>Nouveau PIN (4 chiffres)</label>
              <input
                className="form-control"
                type="password"
                maxLength={4}
                placeholder="****"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              />
            </div>
            <div className="alert alert-info">
              ℹ️ Pas d'email auto. Utilisez "Envoyer PIN par email".
            </div>
          </>
        );

      case "send_pin":
        return (
          <div className="alert alert-info">
            📧 Envoie le PIN actuel par email au client.
          </div>
        );

      case "set_block_percent":
        return (
          <div className="form-grid">
            <div className="form-group">
              <label>Pourcentage de Blocage (0-100%)</label>
              <input
                className="form-control"
                type="number"
                min="0"
                max="100"
                value={blockPercent}
                onChange={(e) => setBlockPercent(Math.min(100, Math.max(0, Number(e.target.value))))}
              />
            </div>

            <div className="form-group">
              <label>Message de Blocage (facultatif)</label>
              <textarea
                className="form-control"
                rows="3"
                placeholder="Ex: Virement en attente validation..."
                value={blockMessage}
                onChange={(e) => setBlockMessage(e.target.value)}
              />
              <small className="text-muted">Affiché côté client.</small>
            </div>

            <div className="alert alert-info">
              <strong>Actuellement:</strong> {acc.block_percent ?? 0}%
              <br />La barre de progression s'arrêtera à ce %.
            </div>
          </div>
        );

      case "edit_activation_code":
        return (
          <>
            <div className="form-group">
              <label>Nouveau Code Activation (4-20 car.)</label>
              <input
                className="form-control"
                maxLength={20}
                placeholder="ABC123XYZ"
                value={newActivationCode}
                onChange={(e) => setNewActivationCode(e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 20))}
              />
              <small className="text-muted">Lettres + chiffres uniquement</small>
            </div>
            <div className="alert alert-info">
              ℹ️ Pas d'email auto. Utilisez "Envoyer TOUTES les infos".
            </div>
          </>
        );

      case "send_activation_code":
        return (
          <div className="alert alert-info">
            📧 Envoie le code activation actuel par email.
          </div>
        );

      case "send_all_info":
        return (
          <div className="alert alert-warning">
            📧 <strong>Email complet</strong>
            <ul>
              <li>✅ IBAN complet</li>
              <li>✅ Code PIN (déchiffré)</li>
              <li>✅ Code activation</li>
              <li>✅ Numéro carte complet</li>
            </ul>
            <p style={{ color: "#b91c1c" }}><strong>⚠️ Informations sensibles !</strong></p>
          </div>
        );

      case "activate":
        return acc.active ? (
          <div className="alert alert-success">✅ Déjà actif.</div>
        ) : (
          <div className="alert alert-info">
            Réactive le compte. Email auto envoyé.
          </div>
        );

      case "archive":
        return (
          <div className="alert alert-warning">
            ⚠️ Désactive le compte. Email auto envoyé.
          </div>
        );

      case "delete":
        return (
          <div className="alert alert-danger">
            🗑️ <strong>Irréversible.</strong> Supprime compte + carte.
            <br />
            <strong>⚠️ Échoue si transactions existent.</strong>
          </div>
        );

      default:
        return <p className="text-muted">Sélectionnez une action.</p>;
    }
  };

  if (authError && !token) {
    return (
      <div className="app-container">
        <div className="card-custom">
          <div className="card-body">
            <div className="alert alert-danger">
              <strong>❌ Accès refusé</strong>
              <p>{authError}</p>
              <small>Connectez-vous avec un compte admin.</small>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <div className="header">
        <div className="header-icon">🏦</div>
        <h1>Gestion des Comptes Bancaires</h1>
        <p>Interface d'administration</p>
      </div>

      {authError && <div className="alert alert-warning">⚠️ {authError}</div>}

      <div className="card-custom">
        <div className="card-body">
          <div className="selection-grid">
            <div>
              <label><span>1️⃣</span> Action</label>
              <select
                className="form-control"
                value={selectedAction}
                onChange={(e) => setSelectedAction(e.target.value)}
              >
                {ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>

            <div>
              <label><span>2️⃣</span> Compte</label>
              <select
                className="form-control"
                value={selectedAccount?.id || ""}
                onChange={(e) => {
                  const acc = items.find(i => i.id === Number(e.target.value));
                  setSelectedAccount(acc);
                }}
                disabled={loading || !selectedAction}
              >
                {loading && <option>⏳ Chargement...</option>}
                {!loading && !selectedAction && <option>Sélectionnez action d'abord</option>}
                {!loading && selectedAction && <option>Choisir un compte...</option>}
                {items.map(acc => (
                  <option key={acc.id} value={acc.id}>
                    {fullName(acc.user)} ({acc.user?.email}) — {fmtMoney(acc.solde, acc.devise)} — {acc.active ? "✅" : "🚫"}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {selectedAccount && (
        <>
          <div className="info-card">
            <h6>📋 Infos compte</h6>
            <div className="info-grid">
              <div><span>Client</span><strong>{fullName(selectedAccount.user)}</strong></div>
              <div><span>Email</span><strong>{selectedAccount.user?.email}</strong></div>
              <div><span>Solde</span><strong className="highlight">{fmtMoney(selectedAccount.solde, selectedAccount.devise)}</strong></div>
              <div><span>IBAN</span><strong className="mono">{formatIban(selectedAccount.iban) || "—"}</strong></div>
              <div><span>Carte</span><strong>{selectedAccount.card?.number_masked || "—"}</strong></div>
              <div><span>Statut</span><span className={`badge ${selectedAccount.active ? "success" : "secondary"}`}>{selectedAccount.active ? "✅ Actif" : "🚫 Inactif"}</span></div>
              {selectedAccount.block_percent > 0 && (
                <div><span>Blocage</span><span className="badge warning">{selectedAccount.block_percent}%</span></div>
              )}
            </div>
          </div>

          {loadingDebits ? (
            <div className="alert alert-info">Chargement transactions...</div>
          ) : pendingDebits.length === 0 ? (
            <div className="alert alert-secondary">Aucun virement en attente pour ce compte.</div>
          ) : (
            <div className="transactions-section">
              <h5>💳 Transactions en attente</h5>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}></th>
                    <th>ID</th>
                    <th>Montant</th>
                    <th>Description</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingDebits.map((t) => (
                    <tr key={t.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedDebits.includes(t.id)}
                          onChange={() => toggleDebitSelection(t.id)}
                        />
                      </td>
                      <td>#{t.id}</td>
                      <td className="amount">-{t.amount} €</td>
                      <td>{t.description || "—"}</td>
                      <td>{new Date(t.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ marginTop: '12px', fontSize: '13px', color: '#64748b' }}>
                {selectedDebits.length > 0 
                  ? `${selectedDebits.length} transaction(s) sélectionnée(s). Saisissez le motif ci-dessous et cliquez sur "Exécuter".`
                  : 'Cochez les transactions à rembourser.'}
              </p>
            </div>
          )}
        </>
      )}

      <div className="card-custom">
        <div className="card-header">
          <h5>{ACTIONS.find(a => a.value === selectedAction)?.label || "Aucune action"}</h5>
        </div>

        <div className="card-body">
          {selectedAction && selectedAccount ? (
            <>
              <ActionForm />
              <div className="actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setSelectedAction("");
                    setSelectedAccount(null);
                  }}
                  disabled={isSubmitting}
                >
                  Annuler
                </button>
                <button
                  className={`btn ${
                    selectedAction === 'delete' ? 'btn-danger' :
                    selectedAction === 'archive' ? 'btn-warning' :
                    selectedAction === 'activate' ? 'btn-success' :
                    'btn-primary'
                  }`}
                  onClick={() => handleAction(selectedAction)}
                  disabled={
                    !selectedAction ||
                    !selectedAccount ||
                    isSubmitting ||
                    (selectedAction === 'activate' && selectedAccount?.active) ||
                    (selectedAction === 'archive' && !selectedAccount?.active)
                  }
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner"></span>
                      Exécution...
                    </>
                  ) : (
                    <>✓ Exécuter</>
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="empty">
              <div className="empty-icon">👆</div>
              <p>Sélectionnez une action et un compte</p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          min-height: 100vh;
        }

        .app-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
        }

        .header {
          text-align: center;
          margin-bottom: 32px;
          animation: fadeIn 0.6s;
        }

        .header-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .header h1 {
          color: white;
          font-size: 32px;
          font-weight: 700;
          margin-bottom: 8px;
          text-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .header p {
          color: rgba(255,255,255,0.9);
          font-size: 16px;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .card-custom {
          background: white;
          border-radius: 16px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.1);
          margin-bottom: 24px;
          overflow: hidden;
        }

        .card-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 16px 24px;
        }

        .card-header h5 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
        }

        .card-body {
          padding: 24px;
        }

        .selection-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 20px;
        }

        @media (min-width: 768px) {
          .selection-grid {
            grid-template-columns: 1fr 1fr;
          }
        }

        label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          color: #334155;
          margin-bottom: 8px;
          font-size: 14px;
        }

        label span {
          font-size: 18px;
        }

        .form-control {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 14px;
          transition: all 0.3s;
          background: white;
        }

        .form-control:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
          outline: none;
        }

        .form-control:disabled {
          background: #f1f5f9;
          opacity: 0.6;
          cursor: not-allowed;
        }

        select.form-control {
          appearance: none;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23334155' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e");
          background-repeat: no-repeat;
          background-position: right 12px center;
          background-size: 16px;
          padding-right: 40px;
        }

        textarea.form-control {
          resize: vertical;
          min-height: 80px;
        }

        .form-grid {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .form-check {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 0;
        }

        .form-check input {
          width: 20px;
          height: 20px;
          cursor: pointer;
          accent-color: #667eea;
        }

        .text-muted {
          color: #64748b;
          font-size: 13px;
          margin-top: 4px;
        }

        .info-card {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 16px;
          padding: 24px;
          color: white;
          margin-bottom: 24px;
          box-shadow: 0 10px 40px rgba(102, 126, 234, 0.3);
        }

        .info-card h6 {
          margin: 0 0 16px 0;
          font-size: 16px;
          font-weight: 600;
        }

        .info-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 12px;
        }

        @media (min-width: 640px) {
          .info-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (min-width: 1024px) {
          .info-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        .info-grid > div {
          background: rgba(255,255,255,0.1);
          padding: 12px;
          border-radius: 10px;
          backdrop-filter: blur(10px);
          display: flex;
          flex-direction: column;
          gap: 6px;
          transition: all 0.3s;
        }

        .info-grid > div:hover {
          background: rgba(255,255,255,0.15);
          transform: translateY(-2px);
        }

        .info-grid span:first-child {
          font-size: 11px;
          opacity: 0.85;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          font-weight: 600;
        }

        .info-grid strong {
          font-size: 14px;
          font-weight: 500;
          word-break: break-word;
        }

        .info-grid .highlight {
          font-size: 18px;
          font-weight: 700;
        }

        .info-grid .mono {
          font-family: 'Courier New', monospace;
          font-size: 13px;
        }

        .badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          width: fit-content;
        }

        .badge.success {
          background: rgba(16, 185, 129, 0.2);
          color: #d1fae5;
        }

        .badge.secondary {
          background: rgba(148, 163, 184, 0.2);
          color: #e2e8f0;
        }

        .badge.warning {
          background: rgba(245, 158, 11, 0.2);
          color: #fef3c7;
        }

        .alert {
          padding: 16px;
          border-radius: 10px;
          margin-bottom: 16px;
          font-size: 14px;
          line-height: 1.6;
        }

        .alert-info {
          background: #dbeafe;
          color: #1e40af;
          border-left: 4px solid #3b82f6;
        }

        .alert-warning {
          background: #fef3c7;
          color: #92400e;
          border-left: 4px solid #f59e0b;
        }

        .alert-danger {
          background: #fee2e2;
          color: #991b1b;
          border-left: 4px solid #ef4444;
        }

        .alert-success {
          background: #d1fae5;
          color: #065f46;
          border-left: 4px solid #10b981;
        }

        .alert-secondary {
          background: #f1f5f9;
          color: #475569;
          border-left: 4px solid #94a3b8;
        }

        .transactions-section {
          background: white;
          border-radius: 16px;
          padding: 24px;
          margin-bottom: 24px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
        }

        .transactions-section h5 {
          margin: 0 0 16px 0;
          font-size: 18px;
          font-weight: 700;
          color: #1e293b;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 10px;
          overflow: hidden;
        }

        thead {
          background: #f1f5f9;
        }

        th, td {
          padding: 12px;
          text-align: left;
          font-size: 14px;
        }

        th {
          font-weight: 600;
          color: #475569;
        }

        tbody tr {
          border-bottom: 1px solid #e2e8f0;
          transition: background 0.2s;
        }

        tbody tr:hover {
          background: #f8fafc;
        }

        tbody tr:last-child {
          border-bottom: none;
        }

        td.amount {
          color: #dc2626;
          font-weight: 700;
        }

        input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
          accent-color: #667eea;
        }

        .actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-top: 24px;
          flex-wrap: wrap;
        }

        @media (max-width: 640px) {
          .actions {
            flex-direction: column-reverse;
          }
          .actions button {
            width: 100%;
          }
        }

        .btn {
          padding: 12px 24px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 14px;
          border: none;
          cursor: pointer;
          transition: all 0.3s;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          white-space: nowrap;
        }

        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        .btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
        }

        .btn-success {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
        }

        .btn-success:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(16, 185, 129, 0.5);
        }

        .btn-warning {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
        }

        .btn-warning:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(245, 158, 11, 0.5);
        }

        .btn-danger {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
        }

        .btn-danger:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(239, 68, 68, 0.5);
        }

        .btn-secondary {
          background: #e2e8f0;
          color: #475569;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #cbd5e1;
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .empty {
          text-align: center;
          padding: 60px 20px;
        }

        .empty-icon {
          font-size: 64px;
          margin-bottom: 16px;
          opacity: 0.6;
        }

        .empty p {
          color: #64748b;
          font-size: 16px;
        }

        ul {
          padding-left: 20px;
          margin: 8px 0;
        }

        li {
          margin-bottom: 4px;
        }

        strong {
          font-weight: 700;
        }

        small {
          font-size: 13px;
        }
      `}</style>
    </div>
  );
}